import { apiService, ApiResponse } from './apiService'
import { tableService } from './tableService'
import type { Filter } from '../types/api.types'
import type {
  UserFeedback,
  FeedbackNote,
  FeedbackWithNotes,
  CreateFeedbackRequest,
  UpdateFeedbackRequest,
  AddFeedbackNoteRequest,
  PresignFeedbackUploadRequest,
  PresignFeedbackUploadResponse
} from '../types/feedback.types'

/**
 * Feedback Service
 *
 * Handles user feedback submission, viewing, and management with screenshot upload support.
 *
 * Workflow:
 * 1. (Optional) presignUpload() - Get presigned S3 URL for screenshot
 * 2. (Optional) uploadToS3() - Upload screenshot to S3
 * 3. createFeedback() - Submit feedback with optional attachment key
 * 4. listFeedback() - View feedback list (RLS-filtered for customers, global for admins)
 * 5. getFeedback() - View single feedback with notes
 * 6. updateFeedback() - Update status/priority/category (admin only)
 * 7. addNote() - Add customer-visible or internal note
 *
 * Integration:
 * - FeedbackModal: User-initiated feedback from Tools menu
 * - ErrorService/ErrorBoundary: Error-initiated feedback with auto-capture
 * - FeedbackManagement: Admin triage and response interface
 */
class FeedbackService {
  private readonly FEEDBACK_ENDPOINT = '/feedback'

  /**
   * Step 1 (Optional): Get presigned S3 URL for screenshot upload
   *
   * @param filename - Original filename
   * @returns Presigned URL and S3 key
   *
   * @example
   * const result = await feedbackService.presignUpload('screenshot.png')
   */
  async presignUpload(
    filename: string
  ): Promise<ApiResponse<PresignFeedbackUploadResponse>> {
    const requestBody: PresignFeedbackUploadRequest = {
      filename
    }

    return await apiService.post<PresignFeedbackUploadResponse>(
      `${this.FEEDBACK_ENDPOINT}/presign`,
      requestBody
    )
  }

  /**
   * Step 2 (Optional): Upload screenshot to S3 using presigned URL
   *
   * @param putUrl - Presigned S3 PUT URL from presignUpload()
   * @param file - Screenshot file to upload
   * @returns Success/failure
   *
   * @example
   * await feedbackService.uploadToS3(presignResponse.putUrl, file)
   */
  async uploadToS3(putUrl: string, file: File): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(putUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'image/png',
        },
      })

      if (!response.ok) {
        return {
          success: false,
          message: `S3 upload failed: ${response.status} ${response.statusText}`,
          errors: [
            {
              code: 'S3_UPLOAD_FAILED',
              message: `HTTP ${response.status}`,
              details: { status: response.status, statusText: response.statusText },
            },
          ],
        }
      }

      return {
        success: true,
        message: 'Screenshot uploaded successfully',
      }
    } catch (error: any) {
      return {
        success: false,
        message: `Upload error: ${error.message}`,
        errors: [
          {
            code: 'UPLOAD_ERROR',
            message: error.message,
            details: error,
          },
        ],
      }
    }
  }

  /**
   * Create feedback submission
   *
   * Context auto-injected from Cognito token (orgId, userId)
   * Browser info auto-captured by backend
   *
   * @param request - Feedback data
   * @returns Created feedback record
   *
   * @example
   * // User-initiated feedback
   * const result = await feedbackService.createFeedback({
   *   feedbackType: 'Bug',
   *   category: 'Pricing',
   *   feedbackComments: 'Price calculation is incorrect',
   *   attachmentKey: 's3-key-from-presign',
   *   attachmentName: 'screenshot.png'
   * })
   *
   * @example
   * // Error-initiated feedback
   * const result = await feedbackService.createFeedback({
   *   feedbackType: 'Error',
   *   feedbackComments: 'I was trying to load the pricing page',
   *   errorCode: 'REACT_ERROR',
   *   errorStack: error.stack,
   *   eventId: 'error-123-abc',
   *   pageUrl: window.location.href
   * })
   */
  async createFeedback(
    request: CreateFeedbackRequest
  ): Promise<ApiResponse<UserFeedback>> {
    return await apiService.post<UserFeedback>(this.FEEDBACK_ENDPOINT, request)
  }

  /**
   * List feedback submissions
   *
   * RLS ensures customers only see their own org's feedback.
   * Admins (rp_admin/rp_support) see all orgs via SECURITY DEFINER function.
   *
   * @param filters - Optional filters for type, status, priority, date range, search
   * @returns Array of feedback records
   *
   * @example
   * const result = await feedbackService.listFeedback()
   * if (result.success) {
   *   console.log(`Found ${result.data.length} feedback items`)
   * }
   */
  async listFeedback(filters?: {
    types?: string[]
    statuses?: string[]
    priorities?: string[]
    categories?: string[]
    dateFrom?: string
    dateTo?: string
    search?: string
    orgId?: string
  }): Promise<ApiResponse<UserFeedback[]>> {
    // Build filter DSL array for backend
    const filterDsl: Filter[] = []

    if (filters?.types?.length) {
      filterDsl.push({
        key: 'feedbackType',
        constraint_type: 'IN',
        values: filters.types
      })
    }
    if (filters?.statuses?.length) {
      filterDsl.push({
        key: 'status',
        constraint_type: 'IN',
        values: filters.statuses
      })
    }
    if (filters?.priorities?.length) {
      const hasUnset = filters.priorities.includes('Unset' as any)
      const realPriorities = filters.priorities.filter(p => p !== 'Unset')

      if (hasUnset && realPriorities.length === 0) {
        // Only "Unset" selected - filter for NULL priority
        filterDsl.push({
          key: 'priority',
          constraint_type: 'IS_NULL',
          value: true
        })
      } else if (!hasUnset && realPriorities.length > 0) {
        // Only real priorities selected - use IN filter
        filterDsl.push({
          key: 'priority',
          constraint_type: 'IN',
          values: realPriorities
        })
      }
      // If both "Unset" + real priorities selected, skip filter (can't do OR in DSL)
    }
    if (filters?.categories?.length) {
      filterDsl.push({
        key: 'category',
        constraint_type: 'IN',
        values: filters.categories
      })
    }
    if (filters?.dateFrom) {
      filterDsl.push({
        key: 'createdAt',
        constraint_type: 'GE',  // Greater than or Equal
        value: filters.dateFrom
      })
    }
    if (filters?.dateTo) {
      filterDsl.push({
        key: 'createdAt',
        constraint_type: 'LE',  // Less than or Equal
        value: filters.dateTo
      })
    }
    if (filters?.search) {
      filterDsl.push({
        key: 'feedbackComments',
        constraint_type: 'CONTAINS',
        value: filters.search
      })
    }
    if (filters?.orgId) {
      // Search by org name (not UID) since user types text
      filterDsl.push({
        key: 'orgName',
        constraint_type: 'CONTAINS',
        value: filters.orgId
      })
    }

    // Use tableService for standard /search endpoint
    return await tableService.search<UserFeedback>(this.FEEDBACK_ENDPOINT, {
      filters: filterDsl,
      order_by: [{ key: 'createdAt', direction: 'desc' }]
    })
  }

  /**
   * Get single feedback item with notes
   *
   * @param uid - Feedback UID
   * @returns Feedback record with notes array
   *
   * @example
   * const result = await feedbackService.getFeedback('feedback-123')
   * if (result.success) {
   *   console.log(`Found ${result.data.notes?.length || 0} notes`)
   * }
   */
  async getFeedback(uid: string): Promise<ApiResponse<FeedbackWithNotes>> {
    return await apiService.get<FeedbackWithNotes>(`${this.FEEDBACK_ENDPOINT}/${uid}`)
  }

  /**
   * Update feedback (admin only)
   *
   * Updates status, priority, category, or assignment.
   * Requires version for optimistic locking.
   *
   * @param request - Update data with version
   * @returns Updated feedback record
   *
   * @example
   * const result = await feedbackService.updateFeedback({
   *   uid: 'feedback-123',
   *   version: 5,
   *   status: 'In Progress',
   *   priority: 'High',
   *   assignedTo: 'jin@rocketpricing.com'
   * })
   */
  async updateFeedback(
    request: UpdateFeedbackRequest
  ): Promise<ApiResponse<UserFeedback>> {
    const { uid, ...updateData } = request
    // Include version in body for optimistic locking (backend extracts it)
    return await apiService.put<UserFeedback>(
      `${this.FEEDBACK_ENDPOINT}/${uid}`,
      updateData  // version is included in updateData
    )
  }

  /**
   * Add note to feedback
   *
   * Customers can only add public notes (internalNote=false).
   * Admins can add both public and internal notes.
   *
   * @param request - Note data
   * @returns Created note record
   *
   * @example
   * // Customer adding public comment
   * const result = await feedbackService.addNote({
   *   feedbackId: 'feedback-123',
   *   noteText: 'This is still happening on the pricing page',
   *   internalNote: false
   * })
   *
   * @example
   * // Admin adding internal note
   * const result = await feedbackService.addNote({
   *   feedbackId: 'feedback-123',
   *   noteText: 'Duplicate of issue #47. Marking as duplicate.',
   *   internalNote: true
   * })
   */
  async addNote(
    request: AddFeedbackNoteRequest
  ): Promise<ApiResponse<FeedbackNote>> {
    const { feedbackId, ...noteData } = request
    return await apiService.post<FeedbackNote>(
      `${this.FEEDBACK_ENDPOINT}/${feedbackId}/notes`,
      noteData
    )
  }

  /**
   * Get notes for a feedback entry
   *
   * @param feedbackId - UID of feedback entry
   * @returns List of notes (filtered by internal_note visibility based on role)
   *
   * @example
   * const result = await feedbackService.getNotes('feedback-123')
   * if (result.data) {
   *   console.log(`Found ${result.data.length} notes`)
   * }
   */
  async getNotes(feedbackId: string): Promise<ApiResponse<FeedbackNote[]>> {
    return await apiService.get<FeedbackNote[]>(
      `${this.FEEDBACK_ENDPOINT}/${feedbackId}/notes`
    )
  }

  /**
   * Get a presigned URL for viewing an attachment
   *
   * @param attachmentKey - S3 key of the attachment
   * @returns Presigned URL valid for 1 hour
   *
   * @example
   * const result = await feedbackService.getAttachmentUrl('feedback-attachments/org-123/uuid/file.png')
   * if (result.data?.viewUrl) {
   *   // Use viewUrl to display the image
   * }
   */
  async getAttachmentUrl(
    attachmentKey: string
  ): Promise<ApiResponse<{ viewUrl: string }>> {
    return await apiService.post<{ viewUrl: string }>(
      `${this.FEEDBACK_ENDPOINT}/presign-view`,
      { key: attachmentKey }
    )
  }
}

// Export singleton instance
export const feedbackService = new FeedbackService()
export default feedbackService
